import { AfterContentInit,  AfterViewInit,  Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core'
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms'
import { DateRange, MatCalendar, MatCalendarCellClassFunction, MAT_RANGE_DATE_SELECTION_MODEL_PROVIDER } from '@angular/material/datepicker'
import { addDays, differenceInDays, eachDayOfInterval, format, isAfter, isSameDay, parseISO, subDays, toDate } from 'date-fns'
import { ReplaySubject, fromEvent } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { formatDate, searchCriteriaFromStorage, toLocalDate, UnsubscribeOnDestroy } from '../../../../utility'
import { ActiveWindow } from '../listing/shared/active-window'
import { TravelDates } from '../listing/shared/travel-dates'

type Calendar = 'checkin' | 'checkout'
const CHECKIN = 'checkin'
const CHECKOUT = 'checkout'

const dateCellClassNames = {
    unavailable: 'mat-datepicker-unavailable',
    checkoutOnly: 'mat-datepicker-checkout-only'
}

@Component ({
    selector: 'app-datepicker',
    templateUrl: 'datepicker.html',
    styleUrls: ['datepicker.scss'],
    providers: [MAT_RANGE_DATE_SELECTION_MODEL_PROVIDER]
})
export class DatepickerComponent extends UnsubscribeOnDestroy implements OnChanges, AfterContentInit, AfterViewInit {

    @Input() id = 'calendar'
    @Input() travelDates: TravelDates
    @Input() inline = false
    @Input() activateCalendar = false
    @Input() border = true
    @Input() unavailableDays: Date[] = []
    @Input() activeWindow: ActiveWindow
    @Input() minimumNights: number | undefined
    @Input() inputStyle: 'rounded' | 'squared' = 'rounded'
    @Input() calendarSmall = false

    private minimumDaysFuture = 7
    datesFormGroup: FormGroup
    futureDay: Date = addDays(toDate(new Date()), this.minimumDaysFuture)
    selectedDateRange: DateRange<Date>
    minCheckinDate: Date = new Date()
    minCheckoutDate: Date = addDays(toDate(this.minCheckinDate), 1)
    maxCheckinDate: Date | undefined
    selectedCheckin: Date | null
    selectedCheckout: Date | null
    activeCalendar: Calendar | undefined
    activeCheckinMonth: Date
    private checkinDate$ = new ReplaySubject<Date | null>(1)
    private checkoutDate$ = new ReplaySubject<Date | null>(1)

    @Output() newDateSearch = new EventEmitter<{checkin: Date, checkout: Date}>()
    @Output() isValid = new EventEmitter<boolean>()
    @Output() checkinMonthChanged = new EventEmitter<Date>()
    @Output() checkoutMonthChanged = new EventEmitter<Date>()
    @Output() calendarClosed = new EventEmitter<Boolean>()

    @ViewChild('checkinCalendar', {static: false}) checkinCalendar: MatCalendar<Date>
    @ViewChild('checkoutCalendar', {static: false}) checkoutCalendar: MatCalendar<Date>

    @HostListener('document:click', ['$event'])
    clickout(event: Event) {
        const targetElement = event.target as HTMLInputElement

        if (!this.eRef.nativeElement.contains(targetElement) && !targetElement.classList.contains('mat-calendar-body-cell-content') && !this.activateCalendar) {
            this.activeCalendar = undefined
            this.calendarClosed.next(true)
        }
   
        this.activateCalendar = false
    }

    @HostListener('document:keydown.escape', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        this.activeCalendar = undefined
        this.activateCalendar = false
        this.calendarClosed.next(true)
    }

    constructor(private eRef: ElementRef<HTMLElement>) {
        super()
    }

    ngAfterContentInit(): void {
        this.initCalendar()

        if (this.activateCalendar) {
            this.activeCalendar = CHECKIN
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            fromEvent(document.querySelectorAll(
                '.checkin-calendar .mat-calendar-previous-button, .checkin-calendar .mat-calendar-next-button'), 'click')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.activeCheckinMonth = this.checkinCalendar.activeDate 
                this.checkinMonthChanged.next(this.checkinCalendar.activeDate)
            })

            fromEvent(document.querySelectorAll(
                '.checkout-calendar .mat-calendar-previous-button, .checkout-calendar .mat-calendar-next-button'), 'click')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.checkoutMonthChanged.next(this.checkoutCalendar.activeDate)
            })
        }, 100)
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.activateCalendar?.previousValue !== changes?.activateCalendar?.currentValue) {
            this.activeCalendar = changes.activateCalendar.currentValue ? CHECKIN : undefined
        }

        if (!changes?.unavailableDays?.firstChange && changes?.unavailableDays?.previousValue !== changes?.unavailableDays?.currentValue) {
            this.checkinCalendar.updateTodaysDate()
            this.checkoutCalendar.updateTodaysDate()
        }

        if (changes?.activeWindow?.previousValue !== changes?.activeWindow?.currentValue) {
            this.activeWindow = changes.activeWindow.currentValue
            const unavailableDayValidator = this.unavailableDayValidator()
            const minimumStayValidator = this.minimumStayValidator()
            this.datesFormGroup.setValidators([Validators.required, unavailableDayValidator, minimumStayValidator])
            this.datesFormGroup.updateValueAndValidity()
        }
    }

    initCalendar() {
        const initCheckin = this.travelDates?.checkin || this.defaultCheckin()
        const initCheckout = this.travelDates?.checkout || this.defaultCheckout()

        this.selectedCheckin = initCheckin
        this.selectedCheckout = initCheckout

        this.initRange()

        const unavailableDayValidator = this.unavailableDayValidator()
        const minimumStayValidator = this.minimumStayValidator()

        this.datesFormGroup = new FormGroup({
            checkin: new FormControl(initCheckin ? formatDate(initCheckin.toString(), 'short') : null),
            checkout: new FormControl(initCheckout ? formatDate(initCheckout.toString(), 'short') : null)
        }, { validators: [Validators.required, unavailableDayValidator, minimumStayValidator] })

        this._subscriptions.push(this.datesFormGroup
            .valueChanges
            .pipe(debounceTime(300))
            .subscribe((event) => {
                event.checkin = event.checkin ? new Date(event.checkin) : null
                event.checkout = event.checkout ? new Date(event.checkout) : null
                this.newDateSearch.next(event)
                this.isValid.next(this.datesFormGroup.valid)
            }))

        this.checkinDate$
            .pipe(takeUntil(this.destroy$))
            .subscribe((checkin: Date | null) => {
                if (checkin !== undefined) {
                    this.minCheckoutDate = addDays(toDate(checkin || new Date()), 1)
                    this.selectedDateRange = new DateRange(checkin, this.selectedCheckout)
                    this.selectedCheckin = checkin
                    this.setNewFormValues(this.selectedCheckin, this.selectedCheckout)

                    if (this.selectedCheckin) {
                        this.checkoutCalendar._goToDateInView(this.selectedCheckin, 'month')
                        this.activeCalendar = CHECKOUT
                    }
                }
            })

        this.checkoutDate$
            .pipe(takeUntil(this.destroy$))
            .subscribe((checkout: Date | null) => {
                if (checkout !== undefined) {
                    if (checkout) {
                        this.maxCheckinDate = addDays(toDate(checkout), -1)
                    } else {
                        this.maxCheckinDate = undefined
                    }
                    this.selectedDateRange = new DateRange(this.selectedCheckin, checkout)
                    this.selectedCheckout = checkout
                    this.setNewFormValues(this.selectedCheckin, this.selectedCheckout)
                }
            })
    }

    initRange() {
        this.selectedDateRange = new DateRange(this.selectedCheckin, this.selectedCheckout)
        if (this.selectedCheckout) {
            this.maxCheckinDate = addDays(toDate(this.selectedCheckout), -1)
        }
        this.minCheckoutDate = addDays(toDate(this.selectedCheckin || new Date()), 1)
    }

    openCheckinCalendar() {
        if (!this.selectedCheckin) {
            this.monthSelected(new Date(), 'checkin')
            this.checkinCalendar._goToDateInView(new Date(), 'month')
        }
        this.activeCalendar = CHECKIN
    }

    openCheckoutCalendar() {
        if (!this.selectedCheckout) {
            this.monthSelected(new Date(), 'checkout')
            this.checkoutCalendar._goToDateInView(new Date(), 'month')
        }
        this.activeCalendar = CHECKOUT
    }

    clearTravelDates() {
        this.setNewFormValues(null, null)
    }

    clearChekinDate() {
        this.setNewFormValues(null, this.selectedCheckout)
    }

    clearCheckoutDate() {
        this.setNewFormValues(this.selectedCheckin, null)
    }

    setNewFormValues(checkin: Date | null, checkout: Date | null) {
        this.datesFormGroup.controls.checkin.setValue(checkin ? formatDate(checkin.toString(), 'short') : null)
        this.datesFormGroup.controls.checkout.setValue(checkout ? formatDate(checkout.toString(), 'short') : null)
    }

    defaultCheckin(): Date | undefined {
        const criteria = searchCriteriaFromStorage()
        if (criteria.checkin) {
            const checkin = parseISO(criteria.checkin)
            if (isSameDay(checkin, this.futureDay) || isAfter(checkin, this.futureDay)) {
                return checkin
            }
        }
    }

    defaultCheckout(): Date | undefined {
        const criteria = searchCriteriaFromStorage()
        if (criteria.checkout) {
            const checkout = parseISO(criteria.checkout)
            if (isAfter(checkout, this.futureDay)) {
                return checkout
            }
        }
    }

    updateCheckinDate(checkin: Date | null) {
        this.checkinDate$.next(checkin)
    }

    updateCheckoutDate(checkout: Date | null) {
        this.checkoutDate$.next(checkout)
    }

    monthSelected($event: Date, type: string) {
        if (type === CHECKIN) {
            this.checkinMonthChanged.next($event)
        } else {
            this.checkoutMonthChanged.next($event)
        }
    }
    
    // Custom date cell classes
    dateClass(): MatCalendarCellClassFunction<Date> {
        return (cellDate: Date) => {
            if (this.activeWindow && !this.activeWindow.includes(cellDate)) {
                return dateCellClassNames.unavailable
            }

            const isUnavailable = this.unavailableDays.find(day => {
                return toLocalDate(day) === toLocalDate(cellDate)
            })

            if (!isUnavailable) {
                return ''
            }

            const index = this.unavailableDays.findIndex(unavailableDay => toLocalDate(unavailableDay) === toLocalDate(cellDate))

            if (index >= 0) {
                // If it's the first of all unavailable days
                if (index === 0) {
                    return dateCellClassNames.checkoutOnly
                }

                const dayBefore = subDays(cellDate, 1)
                const isPreviousDayUnavailable = toLocalDate(this.unavailableDays[index - 1]) === toLocalDate(dayBefore)
                if (isPreviousDayUnavailable) {
                    return dateCellClassNames.unavailable
                }

                const dayAfter = addDays(cellDate, 1)
                const isNextDayUnavailable = this.unavailableDays[index + 1] && toLocalDate(this.unavailableDays[index + 1]) === toLocalDate(dayAfter)
                if (isNextDayUnavailable) {
                    return dateCellClassNames.checkoutOnly
                }
            }
            return dateCellClassNames.unavailable
        }
    }

    private minimumStayValidator(): ValidatorFn {
        return (fg: FormGroup) => {
            let checkin = fg.controls.checkin.value
            let checkout = fg.controls.checkout.value

            if (!checkin) { return null }
            if (!checkout) { return null }
            if (!this.minimumNights) { return null }

            checkin = new Date(checkin)
            checkout = new Date(checkout)

            const nights = differenceInDays(checkout, checkin)
            if (nights < this.minimumNights) {
                return { minimumStayFailed: true }
            }
            return null
        }
    }

    private unavailableDayValidator(): ValidatorFn {
        return (fg: FormGroup) => {
            let checkin = fg.controls.checkin.value
            let checkout = fg.controls.checkout.value

            if (!checkin) { return null }
            if (!checkout) { return null }

            checkin = new Date(checkin)
            checkout = new Date(checkout)

            if (isSameDay(checkin, checkout)) {
                console.log('User selected same date for checkin and checkout - ignoring. Checkin:', checkin)
                return null
            }

            if (isAfter(checkin, checkout)) {
                console.log('User started selected new travel dates - ignoring validation until checkout date is also selected. Checkin:', checkin)
                return null
            }

            if (this.activeWindow && (!this.activeWindow?.includes(checkin) || !this.activeWindow?.includes(checkout))) {
                return { overlapsUnavailableDays: true }
            }

            if (!this.unavailableDays?.length) {
                return null
            }

            // Checkout day is not considered for availability calculation. If the last
            // full travel day is available, a traveller is assumed to check out the next morning
            const lastFullTravelDay = subDays(checkout, 1)
            const travelDays = isSameDay(checkin, lastFullTravelDay)
                ? [checkin]
                : eachDayOfInterval({ start: checkin, end: lastFullTravelDay })
                    .map(date => format(date, 'yyyy-MM-dd'))

            const overlapsWithUnavailableDays = this.unavailableDays
                .map(day => format(day, 'yyyy-MM-dd'))
                .filter(day => travelDays.includes(day))
                .length > 0

            return overlapsWithUnavailableDays
                ? { overlapsUnavailableDays: true }
                : null
            }
    }
}
